import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { QuillModule } from 'ngx-quill';
import TurndownService from 'turndown';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { EnfafSwitchDarkComponent } from '../../../../components/enfaf-switch-dark/enfaf-switch-dark.component';
import { AdminClassNotesView, ApiService } from '../../../../core/services/api/api.service';
import { ToastService } from '../../../../core/services/toast/toast.service';

const NOTES_ERROR_LABELS: Partial<Record<string, string>> = {
  not_found: 'Clase introductoria no encontrada',
  no_transcript: 'Sin transcripción disponible',
  empty_transcript: 'Transcripción vacía',
  ai_error: 'Error del modelo IA',
  pdf_error: 'Error generando PDF',
  s3_error: 'Error subiendo a S3',
  unknown: 'Error desconocido'
};

const TRANSCRIPT_ERROR_FALLBACK = 'Parece que no se ha podido acceder a la transcripción del vídeo. Inténtalo de nuevo más tarde.';
const NOTES_ERROR_FALLBACK = 'Parece que no se ha podido generar el resumen. Inténtalo de nuevo.';
/** Same cadence as AdminLessonNotesComponent's own auto-refresh while processing. */
const POLLING_INTERVAL_MS = 3000;

/**
 * Admin editor for Clase 0's AI summary — same backend pipeline, same editor (Quill) and same
 * interaction model as app-admin-lesson-notes for normal classes: toggling "Mostrar resumen" or
 * editing the text only updates local state (no network round trip, no isLoading flicker); both
 * are persisted together by saveChanges(), called from the parent's general "Guardar" — exactly
 * mirroring AdminLessonNotesComponent.saveChanges(). The only real difference is auto-refresh
 * while processing: reimplemented here as plain polling instead of WebSocket bulk-generation
 * events, which report progress across a whole course's class list and don't apply to Clase 0's
 * single row.
 */
@Component({
  selector: 'app-admin-class-zero-notes',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIconModule, MatProgressSpinnerModule, QuillModule, EnfafSwitchDarkComponent],
  templateUrl: './admin-class-zero-notes.component.html',
  styleUrl: './admin-class-zero-notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminClassZeroNotesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() courseYearId!: string;
  /** Emits whenever the editor or the publish toggle diverges from the last saved state, so the
   * parent AdminClassZeroComponent can fold it into its own "unsaved changes" state — same
   * contract as AdminLessonNotesComponent.notesHasChanges for normal classes. */
  @Output() notesHasChanges = new EventEmitter<boolean>();

  private api = inject(ApiService);
  private toast = inject(ToastService);
  private chRef = inject(ChangeDetectorRef);
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);

  constructor() {
    this.iconRegistry.addSvgIcon('other_icons', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/other_icons.svg'));
    this.iconRegistry.addSvgIcon('other_icons_warning', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/other_icons_warning.svg'));
  }

  readonly notesErrorLabels = NOTES_ERROR_LABELS;
  readonly notesErrorFallback = NOTES_ERROR_FALLBACK;
  readonly transcriptErrorFallback = TRANSCRIPT_ERROR_FALLBACK;

  readonly quillModules = {
    toolbar: [['bold', 'italic', 'underline']]
  };

  private readonly turndown = new TurndownService({ headingStyle: 'atx', bulletListMarker: '-' }).addRule('underline', {
    filter: ['u'],
    replacement: (content: string) => `<u>${content}</u>`
  });

  notesView: AdminClassNotesView | null = null;
  publishedControl = new FormControl<boolean>(false, { nonNullable: true });
  isLoading = false;
  isGeneratingTranscript = false;
  isGeneratingNotes = false;
  isSaving = false;
  isDeleting = false;
  isMarkingReviewed = false;
  isCardExpanded = true;
  editorContent = '';
  savedEditorContent = '';
  savedPublished = false;

  private pollingInterval: ReturnType<typeof setInterval> | null = null;

  get isDirty(): boolean {
    return this.editorContent !== this.savedEditorContent || (this.notesView?.notesPublished ?? false) !== this.savedPublished;
  }

  /** Same "pending review" concept as the normal class's AdminLessonNotesComponent, styled the
   * same way (amber card) — a ready-but-unreviewed summary. */
  get isPendingReview(): boolean {
    return !!this.notesView && this.notesView.notesStatus === 'ready' && !this.notesView.notesReviewedAt;
  }

  /** Same as AdminLessonNotesComponent's isPendingReady — nothing generated yet. Gates the
   * header's generic subtitle: once a summary exists, the header goes quiet like the normal
   * class's does. */
  get isPendingReady(): boolean {
    return !!this.notesView && !this.notesView.notesText && this.notesView.notesStatus !== 'processing';
  }

  toggleCard(): void {
    this.isCardExpanded = !this.isCardExpanded;
  }

  onEditorContentChange(event: { source: string }): void {
    if (event?.source === 'api') return;
    this.notesHasChanges.emit(this.isDirty);
    this.chRef.markForCheck();
  }

  onEditorCreated(quill: any): void {
    const toolbar = quill.getModule('toolbar');
    toolbar.addHandler('italic', (value: boolean) => {
      if (value) quill.format('underline', false);
      quill.format('italic', value);
    });
    toolbar.addHandler('underline', (value: boolean) => {
      if (value) quill.format('italic', false);
      quill.format('underline', value);
    });
  }

  private markdownToHtml(markdown: string): string {
    // Run all content through marked — it passes HTML tags through as-is, so both legacy content
    // and markdown-with-inline-HTML render correctly.
    const raw = marked.parse(markdown, { async: false }) as string;
    return DOMPurify.sanitize(raw);
  }

  ngOnInit(): void {
    void this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['courseYearId'] && !changes['courseYearId'].firstChange) {
      this.stopPolling();
      void this.load();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  async load(): Promise<void> {
    if (!this.courseYearId) return;
    this.isLoading = true;
    this.chRef.markForCheck();
    try {
      const view = await firstValueFrom(this.api.getCourseYearClassZeroNotesView(this.courseYearId));
      this.notesView = view;
      this.publishedControl.setValue(view.notesPublished ?? false, { emitEvent: false });
      this.savedPublished = view.notesPublished ?? false;
      if (view.notesText) {
        this.editorContent = this.markdownToHtml(view.notesText);
        this.savedEditorContent = this.editorContent;
      }
      this.startPollingIfNeeded();
    } catch {
      this.notesView = null;
    } finally {
      this.isLoading = false;
      this.notesHasChanges.emit(this.isDirty);
      this.chRef.markForCheck();
    }
  }

  private startPollingIfNeeded(): void {
    if (this.notesView?.transcriptStatus === 'processing' || this.notesView?.notesStatus === 'processing') {
      this.startPolling();
    }
  }

  /** Own inline fetch-and-merge, deliberately not routed through load() — load() flips isLoading
   * and swaps the whole card body to the loading skeleton, which would flicker every tick while
   * this polls in the background. Same reasoning as AdminLessonNotesComponent's startPolling(). */
  private startPolling(): void {
    this.stopPolling();
    this.pollingInterval = setInterval(async () => {
      try {
        const view = await firstValueFrom(this.api.getCourseYearClassZeroNotesView(this.courseYearId));
        const wasNotesProcessing = this.notesView?.notesStatus === 'processing';
        this.notesView = view;
        const isStillProcessing = view.notesStatus === 'processing' || view.transcriptStatus === 'processing';

        if (wasNotesProcessing && !isStillProcessing && view.notesText) {
          this.editorContent = this.markdownToHtml(view.notesText);
          this.savedEditorContent = this.editorContent;
          this.notesHasChanges.emit(false);
        }

        if (!isStillProcessing) {
          this.stopPolling();
        }
        this.chRef.markForCheck();
      } catch {
        // silent fail during polling
      }
    }, POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval !== null) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /** Called by the parent's general "Guardar" — same contract as
   * AdminLessonNotesComponent.saveChanges(): persists the text edit and/or the publish toggle
   * together, whichever changed. No-ops when there's nothing to persist. */
  async saveChanges(): Promise<void> {
    if (this.isSaving || !this.courseYearId || !this.notesView) return;
    this.isSaving = true;
    this.chRef.markForCheck();
    try {
      const textChanged = this.editorContent !== this.savedEditorContent;
      const publishedChanged = this.notesView.notesPublished !== this.savedPublished;

      if (textChanged) {
        const markdownContent = this.turndown.turndown(this.editorContent);
        const result = await firstValueFrom(this.api.updateCourseYearClassZeroNotesContent(this.courseYearId, markdownContent));
        const newHtml = this.markdownToHtml(result.notesText);
        this.notesView = { ...this.notesView, notesStatus: 'ready', notesText: result.notesText };
        this.editorContent = newHtml;
        this.savedEditorContent = newHtml;
      }

      if (publishedChanged) {
        await firstValueFrom(this.api.toggleCourseYearClassZeroNotesPublished(this.courseYearId, this.notesView.notesPublished));
        this.savedPublished = this.notesView.notesPublished;
      }

      this.notesHasChanges.emit(false);
    } catch {
      this.toast.presentErrorToast('Hubo un error guardando el resumen');
    } finally {
      this.isSaving = false;
      this.chRef.markForCheck();
    }
  }

  async generateTranscript(): Promise<void> {
    if (this.isGeneratingTranscript || !this.courseYearId) return;
    this.isGeneratingTranscript = true;
    this.chRef.markForCheck();
    try {
      await firstValueFrom(this.api.generateCourseYearClassZeroTranscript(this.courseYearId));
      if (this.notesView) {
        this.notesView = { ...this.notesView, transcriptStatus: 'processing' };
      }
      this.startPolling();
    } catch (error: any) {
      this.toast.presentErrorToast(error?.error?.error || 'Hubo un error generando la transcripción');
    } finally {
      this.isGeneratingTranscript = false;
      this.chRef.markForCheck();
    }
  }

  async generateNotes(): Promise<void> {
    if (this.isGeneratingNotes || !this.courseYearId) return;
    this.isGeneratingNotes = true;
    this.chRef.markForCheck();
    try {
      await firstValueFrom(this.api.generateCourseYearClassZeroNotes(this.courseYearId));
      if (this.notesView) {
        this.notesView = { ...this.notesView, notesStatus: 'processing' };
      }
      this.startPolling();
    } catch (error: any) {
      this.toast.presentErrorToast(error?.error?.error || 'Hubo un error generando el resumen');
    } finally {
      this.isGeneratingNotes = false;
      this.chRef.markForCheck();
    }
  }

  /** Local-only, same as AdminLessonNotesComponent.togglePublished() — the actual API call is
   * deferred to saveChanges() so flipping this switch doesn't trigger a network round trip (and
   * the isLoading skeleton flicker that came with it). */
  togglePublished(checked: boolean): void {
    if (!this.notesView) return;
    this.notesView = { ...this.notesView, notesPublished: checked };
    this.notesHasChanges.emit(this.isDirty);
    this.chRef.markForCheck();
  }

  async markAsReviewed(): Promise<void> {
    if (this.isMarkingReviewed || !this.courseYearId) return;
    this.isMarkingReviewed = true;
    this.chRef.markForCheck();
    try {
      const result = await firstValueFrom(this.api.markCourseYearClassZeroNotesReviewed(this.courseYearId));
      if (this.notesView) {
        this.notesView = { ...this.notesView, notesReviewedAt: result.notesReviewedAt, notesReviewedBy: result.notesReviewedBy };
      }
      this.toast.presentSuccessToast('Resumen marcado como revisado');
    } catch {
      this.toast.presentErrorToast('Hubo un error marcando el resumen como revisado');
    } finally {
      this.isMarkingReviewed = false;
      this.chRef.markForCheck();
    }
  }

  async deleteNotes(): Promise<void> {
    if (this.isDeleting || !this.courseYearId) return;
    this.isDeleting = true;
    this.chRef.markForCheck();
    try {
      await firstValueFrom(this.api.deleteCourseYearClassZeroNotes(this.courseYearId));
      this.notesView = this.notesView ? { ...this.notesView, notesStatus: 'pending', notesText: null, notesPublished: false } : null;
      this.publishedControl.setValue(false, { emitEvent: false });
      this.editorContent = '';
      this.savedEditorContent = '';
      this.savedPublished = false;
      this.notesHasChanges.emit(false);
      this.toast.presentSuccessToast('Resumen eliminado');
    } catch {
      this.toast.presentErrorToast('Hubo un error eliminando el resumen');
    } finally {
      this.isDeleting = false;
      this.chRef.markForCheck();
    }
  }
}
